import { BarWidths, FormattedLabels } from '../types';

const NICE_STEPS = [
  1, 2, 5, 10, 15, 20, 30, 60, 90, 120, 180, 240, 300, 360, 480, 600, 720, 1440,
];
const X_AXIS_TICK_COUNT = 5;

/**
 * Calculates the maximum value represented by the x-axis scale.
 * This must match the scale used in calculateXAxisTicks so that
 * bar widths are proportional to the displayed axis.
 */
export const calculateXAxisMax = (
  winDuration: number,
  lossDuration: number,
): number => {
  const maxDuration = Math.max(winDuration, lossDuration);
  if (maxDuration === 0) return 0;
  const roughStep = maxDuration / (X_AXIS_TICK_COUNT - 1);
  const step =
    NICE_STEPS.find((s) => s >= roughStep) ?? NICE_STEPS[NICE_STEPS.length - 1];
  return step * (X_AXIS_TICK_COUNT - 1);
};

/**
 * Calculates bar widths as a percentage of the x-axis scale so that
 * each bar's visual length correctly aligns with the x-axis ticks.
 */
export const calculateBarWidths = (
  winDuration: number,
  lossDuration: number,
): BarWidths => {
  const xAxisMax = calculateXAxisMax(winDuration, lossDuration);

  if (xAxisMax === 0) {
    return { win: 0, loss: 0 };
  }

  return {
    win: (winDuration / xAxisMax) * 100,
    loss: (lossDuration / xAxisMax) * 100,
  };
};

/**
 * Formats a single x-axis tick value in minutes to a readable label.
 * Values < 60 min use "M" suffix; >= 60 min use "H" suffix (rounded).
 * The last tick always gets a "+" appended.
 */
export const formatTickLabel = (minutes: number, isLast: boolean): string => {
  const suffix = isLast ? '+' : '';
  if (minutes === 0) return '0M';
  if (minutes < 60) return `${minutes}M${suffix}`;
  const hours = Math.round(minutes / 60);
  return `${hours}H${suffix}`;
};

/**
 * Derives a nicely-spaced set of 5 x-axis tick labels based on the actual
 * win/loss durations so the scale adapts to the data range.
 *
 * Examples:
 *   win=2.5m, loss=4.3m  → ["0M","2M","4M","6M","8M+"]
 *   win=47m,  loss=379m  → ["0M","2H","4H","6H","8H+"]
 *   win=47m,  loss=1895m → ["0M","8H","16H","24H","32H+"]
 */
export const calculateXAxisTicks = (
  winDuration: number,
  lossDuration: number,
): string[] => {
  const maxDuration = Math.max(winDuration, lossDuration);
  const roughStep = maxDuration / (X_AXIS_TICK_COUNT - 1);
  const step =
    NICE_STEPS.find((s) => s >= roughStep) ?? NICE_STEPS[NICE_STEPS.length - 1];

  return Array.from({ length: X_AXIS_TICK_COUNT }, (_, i) =>
    formatTickLabel(step * i, i === X_AXIS_TICK_COUNT - 1),
  );
};

/**
 * Formats duration in minutes to "XM" format (e.g., 94 -> "94M")
 */
export const formatDurationLabel = (minutes: number): string => {
  return `${Math.round(minutes)}M`;
};

/**
 * Determines hold ratio color based on value
 * Ratio > 1 = unhealthy (holding losers longer) = error (red)
 * Ratio < 1 = healthy (holding winners longer) = success (green)
 */
export const getHoldRatioColor = (ratio: number): 'error' | 'success' => {
  return ratio > 1 ? 'error' : 'success';
};

/**
 * Checks if hold ratio is unhealthy (losers held longer than winners)
 */
export const isUnhealthyRatio = (ratio: number): boolean => {
  return ratio > 1;
};

/**
 * Formats both win and loss duration labels
 */
export const formatDurationLabels = (
  winDuration: number,
  lossDuration: number,
): FormattedLabels => {
  return {
    win: formatDurationLabel(winDuration),
    loss: formatDurationLabel(lossDuration),
  };
};
